<template>
  <div id="endDevice">
    <nav-tabs-extended>
      <template slot="meta-bar-left">
        <span v-if="endDevice.name" :title="endDevice.name" class="max-width mr-4">{{ endDevice.name }}</span>
        <span>{{ devEui }}</span>
        <span class="ml-4">{{ $t('endDevices:statuses.' + endDevice.status) | capitalize }}</span>
        <span v-if="endDevice.lastDataUpDate" class="ml-4 d-none d-sm-inline">{{ $t('ed-endDevice:lastDataUp') | capitalize }}: {{ endDevice.lastDataUpDate | date }}</span>
      </template>

      <template slot="meta-bar-right">
        <span class="ml-lg-4 d-none d-lg-block">{{ endDevice.cluster ? endDevice.cluster.name : '👻' }}</span>
      </template>

      <template slot="tabs-bar">
        <div class="nav-item"><router-link id="endDeviceOverviewLink" :to="{name: 'endDeviceOverview', params: {devEui}}" class="nav-link">{{ $t('ed-endDevice:overview') | capitalize }}</router-link></div>

        <div class="nav-item"><router-link id="endDeviceChannelsLink" :to="{name: 'endDeviceChannels', params: {devEui}}" class="nav-link">{{ $t('ed-endDevice:channels') | capitalize }}</router-link></div>

        <div v-if="$store.getters.wgEnabled" class="nav-item"><router-link id="endDeviceGeolocationLink" :to="{name: 'endDeviceGeolocation', params: {devEui}}" class="nav-link">{{ $t('ed-endDevice:geolocation') | capitalize }}</router-link></div>

        <!-- <div v-if="isRole.admin" class="nav-item"><router-link id="endDeviceMacCommandsLink" :to="{name: 'endDeviceMacCommands', params: {devEui}}" class="nav-link">{{ $t('ed-endDevice:macCommands') | capitalize }}</router-link></div> -->

        <div class="nav-item"><router-link id="endDeviceKpiLink" :to="{name: 'endDeviceKpi', params: {devEui}}" class="nav-link">{{ $t('ed-endDevice:kpi') | capitalize }}</router-link></div>

        <div class="nav-item"><router-link id="endDeviceDataUpLink" :to="{name: 'endDeviceDataUp', params: {devEui}}" class="nav-link">{{ $t('ed-endDevice:dataUp') | capitalize }}</router-link></div>

        <div class="nav-item"><router-link id="endDeviceDataDownLink" :to="{name: 'endDeviceDataDown', params: {devEui}}" class="nav-link">{{ $t('ed-endDevice:dataDown') | capitalize }}</router-link></div>

        <div class="nav-item"><router-link id="endDeviceMessagesLink" :to="{name: 'endDeviceMessages', params: {devEui}}" class="nav-link">{{ $t('ed-endDevice:messages') | capitalize }}</router-link></div>

        <div class="nav-item"><router-link id="endDeviceEventsLink" :to="{name: 'endDeviceEvents', params: {devEui}}" class="nav-link">{{ $t('ed-endDevice:events') | capitalize }}</router-link></div>

        <!-- used to pull the menu to the right -->
        <div style="flex: 1;" />

        <b-dropdown id="end-device-menu" variant="link" no-caret right boundary="window">
          <i slot="button-content" class="icon-more" />

          <b-dropdown-item v-if="this.$store.getters.isEndDeviceInSelection(endDevice)" id="endDeviceRemoveFromSelectionButton" @click="removeFromSelection">{{ $t('ed-endDevice:removeFromSelection') | capitalize }}</b-dropdown-item>
          <b-dropdown-item v-else id="endDeviceAddToSelectionButton" @click="addToSelection">{{ $t('ed-endDevice:addToSelection') | capitalize }}</b-dropdown-item>
          <b-dropdown-item id="endDeviceMenuCopy">{{ $t('ed-endDevice:copy') | capitalize }}</b-dropdown-item>
          <b-dropdown-item v-if="isRole.admin" id="end-device-menu-delete" @click="confirmDeleteEndDevice">{{ $t('ed-endDevice:delete') | capitalize }}</b-dropdown-item>
        </b-dropdown>
      </template>
    </nav-tabs-extended>

    <router-view />

    <confirm id="deleteEndDevice"
             v-model="showDeleteEndDeviceConfirm"
             :message="$t('ed-endDevice:confirmDelete', { endDevice: endDevice.devEui })"
             @confirm:validate="deleteEndDevice"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'
import constants from '@/configuration/constants'
import ClipboardJS from 'clipboard'

import BDropdown from 'bootstrap-vue/es/components/dropdown/dropdown'
import BDropdownItem from 'bootstrap-vue/es/components/dropdown/dropdown-item'

export default {
  components: { BDropdown, BDropdownItem },

  props: {
    devEui: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      clipboard: null,
      refreshTimeout: null,
      showDeleteEndDeviceConfirm: false
    }
  },

  computed: {
    ...mapGetters(['endDevice'])
  },

  watch: {
    devEui () {
      this.getEndDevice()
    },

    endDevice () {
      // set refresh timeout of the end-device
      clearTimeout(this.refreshTimeout)

      // In case the promise ended after the destruction of the component
      // Check the component status to only relaunch the polling when component is not destroyed
      if (this._isDestroyed || this._isBeingDestroyed) return
      this.refreshTimeout = setTimeout(() => { this.getEndDevice() }, constants.endDeviceOverviewRefresh)
    }
  },

  created () {
    if (this.devEui !== this.endDevice.devEui) {
      this.$store.commit('setEndDevice', {})
    }

    this.getEndDevice()
    this.clipboard = new ClipboardJS('#endDeviceMenuCopy', { text: this.getEndDeviceInformation })
  },

  destroyed () {
    clearTimeout(this.refreshTimeout)

    this.clipboard.destroy()
    this.clipboard = null
  },

  methods: {
    getEndDevice () {
      this.$store.dispatch('getEndDevice', this.devEui)
        .then(() => {
          this.$store.commit('addLastViewedEndDevice', this.endDevice)
        })
        .catch((e) => {
          if (e.response && e.response.status === 404) eventBus.$emit('request:404')
          if (e.response && e.response.status === 403) eventBus.$emit('request:403')
        })
    },

    addToSelection () {
      if (this.endDevice) {
        this.$store.commit('addEndDeviceInSelection', this.endDevice)
      }
    },

    removeFromSelection () {
      if (this.endDevice) {
        this.$store.commit('removeEndDeviceFromSelection', this.endDevice)
      }
    },

    getEndDeviceInformation () {
      let edInfo = `Device EUI: ${this.endDevice.devEui}\n`
      edInfo += `Name: ${this.endDevice.name}\n`
      edInfo += `Cluster: ${this.endDevice.cluster ? this.endDevice.cluster.name : '-'}\n`
      edInfo += `Class: ${this.endDevice.classType}\n`
      edInfo += `Region: ${this.endDevice.rfRegion}\n`
      edInfo += `Version: ${this.endDevice.macVersion} ${this.endDevice.regParamsRevision}\n`
      edInfo += `Activation: ${this.endDevice.activation}\n`
      if (this.endDevice.activation === 'OTAA') edInfo += `Application EUI: ${this.endDevice.appEui}\n`
      if (this.endDevice.activation === 'ABP') edInfo += `Device addr: ${this.endDevice.devAddr}\n`
      edInfo += `ADR: ${this.endDevice.adrEnabled}\n`
      edInfo += `Status: ${this.endDevice.status}\n`
      edInfo += `Profile: ${this.endDevice.profile}\n`
      edInfo += `Frame counters:\n`
      edInfo += `\tUp: ${this.endDevice.fcntUp}\n`
      edInfo += `\tDown: ${this.endDevice.fcntDown}\n`
      edInfo += `\n`
      edInfo += `URL: ${location.href}`

      return edInfo
    },

    confirmDeleteEndDevice () {
      this.showDeleteEndDeviceConfirm = true
    },

    async deleteEndDevice () {
      const devEui = this.endDevice.devEui

      let error
      [error] = await to(this.$api.deleteEndDevice(this.endDevice))
      if (error) return

      // remove from selection if it is in
      this.removeFromSelection()
      // reset the store
      this.$store.commit('setEndDevice', {})
      // redirect to the list
      this.$router.replace('/end-devices')
      // Display a success message
      eventBus.$emit('message-success', this.$t('ed-endDevice:deleteSuccess', { endDevice: devEui }))
    }
  }
}
</script>

<style scoped lang="scss">
  .max-width {
    max-width: 20vw;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
